import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { StaffService } from '../staff.service';

type StaffDialogMode = 'create' | 'edit' | 'self';

@Component({
  selector: 'app-staff-dialog',
  templateUrl: './staff-dialog.component.html',
  styleUrls: ['./staff-dialog.component.scss']
})
export class StaffDialogComponent {
  form: FormGroup;
  loading = false;
  mode: StaffDialogMode;
  users: any[] = [];
  readonly positions: string[] = [
    'Executive Directors',
    'Managing Directors',
    'Senior Staffs',
    'Riders',
    'Contract'
  ];

  constructor(
    private fb: FormBuilder,
    private service: StaffService,
    private dialogRef: MatDialogRef<StaffDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.mode = data?.mode || (data?.staff ? 'edit' : 'create');
    const staff = data?.staff || {};
    this.users = data?.users || [];

    const normalizedPosition = staff.position === 'Managers Dirrctors' ? 'Managing Directors' : (staff.position || '');

    this.form = this.fb.group({
      employeeId: [staff.employeeId || ''],
      fullName: [staff.fullName || '', Validators.required],
      email: [staff.email || '', Validators.email],
      phoneNumber: [staff.phoneNumber || ''],
      department: [staff.department || ''],
      position: [normalizedPosition],
      employmentType: [staff.employmentType || ''],
      hireDate: [staff.hireDate ? this.toDateInput(staff.hireDate) : ''],
      address: [staff.address || ''],
      isActive: [staff.isActive ?? true],
      userId: [staff.userId || '']
    });

    if (this.mode === 'self') {
      this.form.get('employeeId')?.disable();
      this.form.get('email')?.disable();
      this.form.get('isActive')?.disable();
      this.form.get('userId')?.disable();
    }
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isSelfMode(): boolean {
    return this.mode === 'self';
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = this.getPayload();

    const req$ =
      this.mode === 'create'
        ? this.service.create(payload)
        : this.mode === 'edit'
        ? this.service.update(this.data.staff.id, payload)
        : this.service.updateMyProfile(payload);

    req$.subscribe({
      next: () => {
        this.loading = false;
        Swal.fire('Success', this.mode === 'create' ? 'Staff created.' : 'Staff updated.', 'success');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save staff data.', 'error');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private getPayload(): any {
    const raw = this.form.getRawValue();
    if (this.mode === 'self') {
      return {
        fullName: raw.fullName,
        phoneNumber: raw.phoneNumber,
        department: raw.department,
        position: raw.position,
        employmentType: raw.employmentType,
        hireDate: raw.hireDate || null,
        address: raw.address
      };
    }

    return {
      employeeId: raw.employeeId || null,
      fullName: raw.fullName,
      email: raw.email || null,
      phoneNumber: raw.phoneNumber || null,
      department: raw.department || null,
      position: raw.position || null,
      employmentType: raw.employmentType || null,
      hireDate: raw.hireDate || null,
      address: raw.address || null,
      isActive: !!raw.isActive,
      userId: raw.userId || null
    };
  }

  private toDateInput(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }
}
